// Ficha de documentos de un vehiculo o conductor en la planta seleccionada
// (PP-23). Muestra cada tipo que la planta exige, con estado y vencimiento, y
// los demas papeles que el sujeto tenga. Desde aca el proveedor carga lo que
// falta y renueva lo que vence, sin pasar por UCORP.
//
// Un documento se carga una sola vez (ADR-014 §2): un tipo que el sujeto ya
// tiene se renueva (PATCH sobre el vigente), nunca se duplica. Lo decide
// uploadPlan, tambien cuando el tipo se escribe a mano.
//
// La elegibilidad la calcula la API: despues de guardar se relee la nomina
// (onChanged) y el badge cambia con lo que ella devuelva.
import { useCallback, useEffect, useState } from "react";
import type { DocumentSubject, Eligibility } from "@ucorp/contracts";
import type { PortalSession } from "../../auth/session.js";
import { Alert } from "../../components/ui/alert.js";
import { Button } from "../../components/ui/button.js";
import { Failure } from "../../components/ui/failure.js";
import { SkeletonRows } from "../../components/ui/skeleton.js";
import {
  documentRows,
  documentTypeLabel,
  expiryDetail,
  formatCalendarDate,
  requiredTypesFor,
  uploadPlan,
  uploadRequest,
  type UploadDraft,
} from "../../logic/documents.js";
import { EligibilityBadge } from "../parts.js";
import { FleetError, type FleetSite } from "../port.js";
import type { DocumentsPort, SiteDocuments } from "./port.js";
import { DocumentForm, DocumentStatusBadge, documentErrorCopy } from "./parts.js";

interface Props {
  session: PortalSession;
  port: DocumentsPort;
  site: FleetSite;
  subject: DocumentSubject;
  /** "Vehículo M-01 · AA100AA" o "Conductor M-01 · Nombre". */
  subjectLabel: string;
  eligibility: Eligibility;
  /** Despues de cargar o renovar: relee la nomina para el badge de elegibilidad. */
  onChanged: () => void;
  onClose: () => void;
}

type LoadState = { estado: "cargando" } | { estado: "denegado" } | { estado: "error" } | { estado: "listo"; data: SiteDocuments };

/** Que formulario esta abierto: el de una fila (tipo fijo) o el de otro documento (tipo libre). */
type Editing = { type: string; mode: "create" | "renew" } | { type: undefined; mode: "create" } | null;

export function SubjectDocuments({ session, port, site, subject, subjectLabel, eligibility, onChanged, onClose }: Props): React.JSX.Element {
  const [state, setState] = useState<LoadState>({ estado: "cargando" });
  const [round, setRound] = useState(0);
  const [editing, setEditing] = useState<Editing>(null);
  // El resultado se muestra dentro de la hoja, donde esta la atencion del
  // operador; un toast quedaria atras de la hoja modal.
  const [notice, setNotice] = useState<{ tone: "info" | "error"; text: string } | undefined>(undefined);
  const { siteId, providerId } = site;
  const { type: subjectType, id: subjectId } = subject;

  useEffect(() => {
    let current = true;
    port
      .listDocuments(session, { siteId, providerId }, { subjectType, subjectId })
      .then((data) => {
        if (current) setState({ estado: "listo", data });
      })
      .catch((error: unknown) => {
        if (current) setState({ estado: error instanceof FleetError && error.kind === "access" ? "denegado" : "error" });
      });
    return () => {
      current = false;
    };
  }, [port, session, siteId, providerId, subjectType, subjectId, round]);

  const save = useCallback(
    async (draft: UploadDraft): Promise<string | undefined> => {
      if (state.estado !== "listo") return documentErrorCopy(undefined);
      const plan = uploadPlan(state.data.documents, subject, draft.type);
      const request = uploadRequest(draft, subject, plan);
      if (request === null) return "Falta el tipo de documento o la fecha no es válida.";
      try {
        const saved =
          request.kind === "renew"
            ? await port.updateDocument(session, { siteId, providerId }, request.documentId, request.body)
            : await port.createDocument(session, { siteId, providerId }, request.body);
        setEditing(null);
        setNotice({ tone: "info", text: `${documentTypeLabel(saved.type)} ${request.kind === "renew" ? "renovado" : "cargado"}.` });
        setRound((n) => n + 1);
        onChanged();
        return undefined;
      } catch (error) {
        // Un 409 es un papel que otro cargo mientras tanto: se relee para que
        // la fila pase a "Renovar" y nadie cargue un duplicado.
        if (error instanceof FleetError && error.kind === "conflict") {
          setEditing(null);
          setNotice({ tone: "error", text: documentErrorCopy(error) });
          setRound((n) => n + 1);
          return undefined;
        }
        return documentErrorCopy(error);
      }
    },
    [state, subject, port, session, siteId, providerId, onChanged],
  );

  const titleId = `docs-${subject.type}-${subject.id}`;

  // Vive dentro de la hoja lateral, que ya muestra de quien es la ficha y
  // tiene su Cerrar: el titulo queda como nombre accesible de la region.
  return (
    <section className="flex flex-col gap-3" aria-labelledby={titleId} role="region">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id={titleId} className="sr-only">
          Documentos de {subjectLabel}
        </h2>
        <div className="flex items-center gap-2 text-xs text-texto-suave">
          Elegibilidad en la planta
          <EligibilityBadge eligibility={eligibility} />
        </div>
        <Button size="sm" variant="fantasma" onClick={onClose}>
          Cerrar
        </Button>
      </div>

      {notice !== undefined && <Alert variant={notice.tone}>{notice.text}</Alert>}
      {state.estado === "cargando" && <SkeletonRows rows={3} label="Consultando documentos" />}
      {state.estado === "denegado" && <Failure title="La cuenta no tiene acceso a los documentos de este vehículo o conductor." />}
      {state.estado === "error" && <Failure title="No se pudieron consultar los documentos. Reintentar en unos minutos." onRetry={() => setRound((n) => n + 1)} />}

      {state.estado === "listo" && (
        <DocumentTable
          data={state.data}
          subject={subject}
          subjectLabel={subjectLabel}
          eligibility={eligibility}
          editing={editing}
          onEdit={(next) => {
            setNotice(undefined);
            setEditing(next);
          }}
          onSave={save}
        />
      )}
    </section>
  );
}

function DocumentTable({
  data,
  subject,
  subjectLabel,
  eligibility,
  editing,
  onEdit,
  onSave,
}: {
  data: SiteDocuments;
  subject: DocumentSubject;
  subjectLabel: string;
  eligibility: Eligibility;
  editing: Editing;
  onEdit: (editing: Editing) => void;
  onSave: (draft: UploadDraft) => Promise<string | undefined>;
}): React.JSX.Element {
  const required = requiredTypesFor(data.requiredDocuments, subject.type, eligibility);
  const rows = documentRows(required, data.documents, subject);

  return (
    <div className="flex flex-col gap-3">
      {data.requiredDocuments === undefined && (
        <Alert variant="aviso">
          No se pudo saber qué documentos exige la planta: se muestran los que faltan o vencieron y los que ya están cargados.
        </Alert>
      )}
      {data.requiredDocuments !== undefined && required.length === 0 && (
        <Alert variant="info">La planta no exige documentos a {subject.type === "vehicle" ? "los vehículos" : "los conductores"}.</Alert>
      )}
      {rows.length > 0 && (
        <div className="overflow-x-auto rounded-md border border-linea">
          <table className="tabla-fichas w-full border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-divisor bg-hundida/60">
                {["Documento", "Estado", "Vencimiento", "Referencia", "Acciones"].map((header) => (
                  <th key={header} scope="col" className="rotulo h-9 whitespace-nowrap px-3 text-texto-suave">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const mode = row.document === undefined ? "create" : "renew";
                const label = documentTypeLabel(row.type);
                return (
                  <tr key={row.type} className="border-b border-divisor align-middle last:border-b-0">
                    <td data-rotulo="Documento" className="px-3 py-1.5 text-texto">
                      <div className="flex flex-col">
                        <span>{label}</span>
                        {!row.required && <span className="text-xs text-texto-suave">No lo exige esta planta</span>}
                      </div>
                    </td>
                    <td data-rotulo="Estado" className="px-3 py-1.5">
                      <DocumentStatusBadge status={row.status} />
                    </td>
                    <td data-rotulo="Vencimiento" className="px-3 py-1.5 text-texto">
                      {row.document === undefined ? (
                        <span className="text-xs text-texto-suave">Sin cargar</span>
                      ) : (
                        <div className="flex flex-col">
                          <span className="tabular">{row.document.expiresAt === null ? "No vence" : formatCalendarDate(row.document.expiresAt)}</span>
                          <span className="text-xs text-texto-suave">{expiryDetail(row.document.expiresAt, data.today)}</span>
                        </div>
                      )}
                    </td>
                    <td data-rotulo="Referencia" className="tabular px-3 py-1.5 text-texto">{row.document?.reference ?? <span className="text-xs text-texto-suave">Sin dato</span>}</td>
                    <td data-rotulo="Acciones" className="px-3 py-1.5">
                      <Button
                        size="sm"
                        variant={mode === "create" ? "primario" : "secundario"}
                        aria-label={`${mode === "create" ? "Cargar" : "Renovar"} ${label}`}
                        disabled={editing !== null}
                        onClick={() => onEdit({ type: row.type, mode })}
                      >
                        {mode === "create" ? "Cargar" : "Renovar"}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null && (
        <DocumentForm
          key={editing.type ?? "otro"}
          mode={editing.mode}
          type={editing.type}
          subjectLabel={subjectLabel}
          onSubmit={onSave}
          onCancel={() => onEdit(null)}
        />
      )}

      {editing === null && (
        <div>
          <Button size="sm" variant="fantasma" onClick={() => onEdit({ type: undefined, mode: "create" })}>
            Cargar otro documento
          </Button>
        </div>
      )}
    </div>
  );
}
